import { setPosts, appendPosts, setLoading } from "../store/slices/PostSlice";
import { setShorts, appendShorts } from "../store/slices/ShortSlice";
import { setPlaces, appendPlaces } from "../store/slices/PlacesSlice";
import { request } from "../Utils/request";
import { getPublicUrlFromFileKey } from "./API/Api";

 export const extractHashtags = (text: string): string[] => {
    const hashtagRegex = /#[a-zA-Z0-9][a-zA-Z0-9_]*/g;
    const matches = text.match(hashtagRegex) || [];
    return matches.map(tag => tag.slice(1).toLowerCase());
  };

  export const followUser = async (id: string) => {
    const res = await request(`/profile/${id}/follow`, {
      method: 'POST',
      authorization: true,
    }) as any;
    return res.data;
  }

  export const getAllPublicUrls = async (filekeys: string[]) => {
    const responses = await Promise.all(
        filekeys.map(key => getPublicUrlFromFileKey(key, 'post'))
    );
    return responses.map(res => res.data.files[0].viewUrl);
};

export const likePost = async (id: string) => {
  try {
    const response = await request(`/posts/${id}/like`, {
      method: 'POST',
      authorization: true,
    }) as any;
    return response;
  } catch (error) {
    console.log('Error liking post:', error);
    throw error;
  }
};

export const bookmarkPost = async (id: string) => {
  const response = await request(`/profile/saved-posts/${id}`, {
    method: 'POST',
    authorization: true,
  }) as any;
  return response;
};

export const fetchPosts = async (
  dispatch: any,
  fileType: string,
  page: number = 1,
  limit: number = 10,
  append: boolean = false,
  postsReelsByLocation: boolean = false,
  location?: { latitude: number; longitude: number }
) => {
  try {
      dispatch(setLoading(true));
      const locationParams =
        postsReelsByLocation && location
          ? `&longitude=${location.longitude}&latitude=${location.latitude}`
          : '';
      const response = await request<any>(`/posts/?fileType=${fileType}&page=${page}&limit=${limit}${locationParams}`, {
          method: 'GET',
          authorization: true,
      });
      const postData = response?.data;
      
      if(fileType === 'image'){
        if(append) {
          dispatch(appendPosts(postData));
        } else {
          dispatch(setPosts(postData));
        }
      } else {
        if(append) {
          dispatch(appendShorts(postData));
        } else {
          dispatch(setShorts(postData));
        }
      }
  } catch (error) {
      console.error('Error fetching posts:', error);
  } finally {
      dispatch(setLoading(false));
  }
};

export const fetchPlaces = async (
  dispatch: any, 
  page: number = 1, 
  limit: number = 5, 
  append: boolean = false,
  options?: {
    longitude?: number;
    latitude?: number;
    maxDistance?: number;
    category?: string;
    search?: string;
    minRating?: number;
    sortBy?: string;
  }
): Promise<any[]> => {
  try {
      dispatch(setLoading(true));
      
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());
      
      // Add optional parameters
      if (options?.longitude !== undefined) {
        queryParams.append('longitude', options.longitude.toString());
      }
      if (options?.latitude !== undefined) {
        queryParams.append('latitude', options.latitude.toString());
      }
      if (options?.maxDistance !== undefined) {
        queryParams.append('maxDistance', options.maxDistance.toString());
      }
      if (options?.category) {
        queryParams.append('category', options.category);
      }
      if (options?.search) {
        queryParams.append('search', options.search);
      }
      if (options?.minRating !== undefined) {
        queryParams.append('minRating', options.minRating.toString());
      }
      if (options?.sortBy) {
        queryParams.append('sortBy', options.sortBy);
      }
      
      const response = await request<any>(`/places?${queryParams.toString()}`, {
          method: 'GET',
          authorization: true,
      });
      const placesData = response?.data || [];
      
      if(append) {
        dispatch(appendPlaces(placesData));
      } else {
        dispatch(setPlaces(placesData));
      }
      
      return placesData;
  } catch (error) {
      console.error('Error fetching places:', error);
      return [];
  } finally {
      dispatch(setLoading(false));
  }
};

export const fetchUserPosts = async (
  dispatch: any,
  userId: string,
  page: number = 1,
  limit: number = 12,
  append: boolean = false,
  options?: { type?: string }
) => {
  try {
      dispatch(setLoading(true));
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (options?.type) {
        queryParams.append('type', options.type);
      }

      const response = await request<any>(`/posts/user/${userId}?${queryParams.toString()}`, {
          method: 'GET',
          authorization: true,
      });
      const postData = response?.data?.posts || response?.data || [];
      
      if(append) {
        dispatch(appendPosts(postData));
      } else {
        dispatch(setPosts(postData));
      }
  } catch (error) {
      console.error('Error fetching user posts:', error);
      // Try fallback endpoint
      try {
        const fallbackQueryParams = new URLSearchParams({
          userId,
          page: page.toString(),
          limit: limit.toString(),
        });

        if (options?.type) {
          fallbackQueryParams.append('type', options.type);
        }

        const fallbackResponse = await request<any>(`/posts/?${fallbackQueryParams.toString()}`, {
          method: 'GET',
          authorization: true,
        });
        const fallbackData = fallbackResponse?.data?.posts || fallbackResponse?.data || [];
        
        if(append) {
          dispatch(appendPosts(fallbackData));
        } else {
          dispatch(setPosts(fallbackData));
        }
      } catch (fallbackError) {
        console.error('Fallback endpoint also failed:', fallbackError);
      }
  } finally {
      dispatch(setLoading(false));
  }
};

export const fetchExploreTrips = async (
  page: number = 1,
  limit: number = 5,
  search?: string,
  category?: string,
  type?: 'trip' | 'itinerary'
) => {
  try {
    let endpoint: string;
    let queryParams: URLSearchParams;

    if (search) {
      // Call /landing/search when there's a search term
      queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: search,
      });
      endpoint = `/landing/search?${queryParams.toString()}`;
    } else {
      // Call /profile/explorer when there's no search term
      queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(category && category !== 'All' && { category }),
        ...(type && { type }),
      });
      endpoint = `/profile/explorer?${queryParams.toString()}`;
    }

    const response = await request<{ 
      success: boolean; 
      data: any[], 
      message: string;
      pagination?: {
        currentPage: number;
        totalPages: number;
        hasNextPage: boolean;
      }
    }>(endpoint, {
      method: 'GET',
      authorization:true
    });

    return {
      data: response?.data || [],
      pagination: response?.pagination,
      hasMore: response?.pagination?.hasNextPage ?? (response?.data?.length === limit)
    };
  } catch (error) {
    console.error('Error fetching explore trips:', error);
    throw error;
  }
};